import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  Alert,
  ScrollView,
  Animated,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  TextInput,
  Image,
  ImageBackground,
  StatusBar,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Page, QuestionAnswer } from '../../services/discoveryOnboardingAPI';
import { 
  PageContainer, 
  Text, 
  Button,
  StableScrollView,
  SafariOptimizedContainer,
  Theme,
  OnboardingPageContainer
} from '../../components/ui';
import { RootStackParamList } from '../../navigation/RootNavigator';
import AudioRecorder from '../../components/AudioRecorder';
import LearnByDoingPage from './components/LearnByDoingPage';
import { HapticFeedback } from '../../utils/haptics';
import { loggingService } from '../../services/loggingService';
import { 
  onboardingStarted, 
  onboardingQuestionAnswered, 
  onboardingCompleted 
} from '../../utils/logging/actions';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';

type DiscoveryV2OnboardingNavigationProp = NativeStackNavigationProp<RootStackParamList, 'DiscoveryV2Onboarding'>;

interface PageInputState {
  selectedAnswers: string[];
  textAnswer: string;
  customAnswer: string;
  showCustomInput: boolean;
}

// Helper function to convert question title to a slug for logging
function getQuestionSlug(title: string, type: string): string {
  // Create slug from title - lowercase, replace spaces with underscores, remove special chars
  const baseSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .substring(0, 50); // Limit length
  
  // Use type as prefix for uniqueness
  return `${type}_${baseSlug}`;
}

export default function DiscoveryV2OnboardingScreen() {
  useNavigationLogging('DiscoveryV2OnboardingScreen');
  
  const navigation = useNavigation<DiscoveryV2OnboardingNavigationProp>();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<Page[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<QuestionAnswer[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [textAnswer, setTextAnswer] = useState('');
  const [customAnswer, setCustomAnswer] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isProcessingSingleSelect, setIsProcessingSingleSelect] = useState(false);
  const [showLearnByDoingPage, setShowLearnByDoingPage] = useState(false);
  const [isLoadingMoreQuestions, setIsLoadingMoreQuestions] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pageStateMap, setPageStateMap] = useState<Record<number, PageInputState>>({});
  const [pageAnswerMap, setPageAnswerMap] = useState<Record<number, QuestionAnswer>>({});
  const slideAnim = useRef(new Animated.Value(1)).current;
  const scrollViewRef = useRef<ScrollView>(null);
  const onboardingStartTime = useRef(Date.now());
  const hasLoggedStart = useRef(false);
  
  // Add debouncing state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<(() => void) | null>(null);

  // Initialize flow
  useEffect(() => {
    setLoading(true);
    // Local guru images (static requires). Works on native and web bundlers.
    const GURU_IMAGES: Record<string, any> = {
      elon_musk: require('../../assets/onboarding/gurus/elon_musk.jpg'),
      steve_jobs: require('../../assets/onboarding/gurus/steve_jobs.jpg'),
      tony_robbins: require('../../assets/onboarding/gurus/tony_robbins.jpg'),
      oprah_winfrey: require('../../assets/onboarding/gurus/oprah_winfrey.jpg'),
      warren_buffett: require('../../assets/onboarding/gurus/warren_buffett.jpg'),
      bill_gates: require('../../assets/onboarding/gurus/bill_gates.jpg'),
      michelle_obama: require('../../assets/onboarding/gurus/michelle_obama.jpg'),
      marcus_aurelius: require('../../assets/onboarding/gurus/marcus_aurelius.jpg'),
      simon_sinek: require('../../assets/onboarding/gurus/simon_sinek.jpg'),
      jordan_peterson: require('../../assets/onboarding/gurus/jordan_peterson.jpg'),
      barack_obama: require('../../assets/onboarding/gurus/barack_obama.jpg'),
      buddha: require('../../assets/onboarding/gurus/buddha.jpg'),
    };
    const localPages: Page[] = [
      {
        type: 'interrupter',
        title: 'Welcome! I\'m your personal mentor',
        subtitle: 'If you feel stuck or overwhelmed — you\'re not alone.\n\n1️⃣ Tell me what you\'re dealing with\n2️⃣ I build your path\n3️⃣ You get one clear Step for today',
        buttonText: 'Let\'s begin',
        image: require('../../assets/onboarding/onboarding1.webp'),
      } as any,
      {
        type: 'closed_question',
        title: 'What brings you to Mentorist?',
        subtitle: 'Choose what resonates most with you right now.',
        answers: [
          { id: 'apply', label: 'Apply what I learn' },
          { id: 'procrastinating', label: 'Stop procrastinating' },
          { id: 'progress', label: 'Make real progress' },
          { id: 'guidance', label: 'Get personalized guidance' },
        ],
      },
      {
        type: 'interrupter',
        title: 'One small Step each day',
        subtitle: 'Your mentor adapts to you and remembers your context — grounded in the best books. Chat anytime for guidance.',
        buttonText: 'Continue',
        image: require('../../assets/onboarding/value-prop/solution.webp'),
      } as any,
      {
        type: 'open_question',
        title: 'What should I call you?',
        subtitle: 'I\'ll use your name when we set goals and celebrate wins.',
        placeholder: 'Your name',
        maxLength: 50,
      },
      {
        type: 'age',
        title: "What's your age?",
        subtitle: 'This helps me tailor content to your life stage.',
        answers: [
          { id: '18-24', label: '18-24' },
          { id: '25-34', label: '25-34' },
          { id: '35-44', label: '35-44' },
          { id: '45-54', label: '45-54' },
          { id: '55-64', label: '55-64' },
          { id: '65+', label: '65+' },
        ],
      },
      {
        type: 'gender',
        title: 'How do you identify?',
        subtitle: 'This helps me personalize examples and recommendations.',
        answers: [
          { id: 'male', label: 'Male' },
          { id: 'female', label: 'Female' },
          { id: 'prefer_not_to_say', label: 'Prefer not to say' },
        ],
      },
      {
        type: 'closed_question',
        title: 'Who are you?',
        subtitle: 'Choose any that apply. We\'ll tailor recommendations for you.',
        maxSelections: 3,
        allowSkip: true,
        answers: [
          { id: 'parents', label: 'Parent' },
          { id: 'students', label: 'Student' },
          { id: 'entrepreneurs', label: 'Entrepreneur' },
          { id: 'startup_founders', label: 'Startup Founder' },
          { id: 'managers', label: 'Manager' },
          { id: 'leaders', label: 'Leader' },
        ],
      },
      {
        type: 'interrupter',
        title: 'Great! Your profile is taking shape',
        subtitle: 'Now let\'s match you with the right content and coaching style.',
        buttonText: 'Continue',
        image: require('../../assets/onboarding/onboarding2.webp'),
      } as any,
      {
        type: 'closed_question',
        title: 'Your Growth Focus',
        subtitle: 'I\'ll recommend books and content in these areas.',
        answers: [
          { id: 'personal', label: 'Personal development & life balance' },
          { id: 'business', label: 'Business & career advancement' },
          { id: 'both', label: 'Both' },
          { id: 'exploring', label: "I'm exploring what I need most" },
        ],
      },
      {
        type: 'closed_question',
        title: 'Your Learning Style',
        subtitle: 'I\'ll match content to how you like to learn.',
        answers: [
          { id: 'tactics', label: '🎯 Practical business strategies & tactics' },
          { id: 'stories', label: '✨ Personal transformation & life stories' },
          { id: 'research', label: '🧠 Research-based psychology & neuroscience' },
          { id: 'philosophy', label: '🧘 Philosophy & spiritual wisdom' },
          { id: 'mix', label: '🌈 Mix of everything' },
        ],
      },
      {
        type: 'closed_question',
        title: 'Your Preferred Coaching Style',
        subtitle: 'I\'ll adapt my tone and approach to match your preference.',
        answers: [
          { id: 'direct', label: 'Direct and challenging' },
          { id: 'supportive', label: 'Supportive and encouraging' },
          { id: 'data_driven', label: 'Data-driven and logical' },
          { id: 'inspirational', label: 'Inspirational and visionary' },
          { id: 'practical', label: 'Practical and actionable' },
          { id: 'mix', label: 'Mix' },
        ],
      },
      {
        type: 'interrupter',
        title: 'Almost there ✨',
        subtitle: 'Now let\'s talk about what really matters. These questions will help me find the right content and create action steps for you.',
        buttonText: 'Continue',
        image: require('../../assets/onboarding/onboarding3.webp'),
      } as any,
      {
        type: 'closed_question',
        title: 'Which area feels most important to grow right now?',
        subtitle: 'I\'ll find solutions specific to your biggest priority.',
        answers: [
          { id: 'leadership', label: 'Leadership & Influence' },
          { id: 'confidence', label: 'Confidence & Self-Worth' },
          { id: 'time_mgmt', label: 'Time Management & Productivity' },
          { id: 'purpose', label: 'Purpose & Meaning' },
          { id: 'relationships', label: 'Relationships & Communication' },
        ],
      },
      {
        type: 'open_question',
        title: 'What\'s your biggest struggle right now?',
        subtitle: 'I\'ll use this to match you with the right books and create steps to move past it.',
        placeholder: 'E.g., "I keep procrastinating on important work" or "I feel overwhelmed balancing work and family"',
        maxLength: 5000,
      },
      {
        type: 'open_question',
        title: 'What tends to get in the way?',
        subtitle: 'Share what makes this hard so I can target solutions for you.',
        placeholder: 'E.g., "I overthink decisions" or "Too many distractions and no routine"',
        maxLength: 5000,
      },
      {
        type: 'interrupter',
        title: 'You are not alone',
        subtitle: "Many people feel exactly how you feel right now. You're taking a powerful first step — and that's what matters.",
        buttonText: 'Continue',
        image: require('../../assets/onboarding/interruptor-notalone.webp'),
      } as any,
    ];

    setPages(localPages);
    setLoading(false);
    
    // Log onboarding started (only once)
    if (!hasLoggedStart.current) {
      hasLoggedStart.current = true;
      loggingService.log(onboardingStarted('discovery_v2'));
    }
  }, []);

  // Scroll to top when page changes
  useEffect(() => {
    // Small delay to ensure the content has rendered
    const timer = setTimeout(() => {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [currentPageIndex]);

  const currentPage = pages[currentPageIndex];

  // Light slide/fade when page index changes
  useEffect(() => {
    slideAnim.setValue(0);
    Animated.timing(slideAnim, {
      toValue: 1,
      duration: 220,
      useNativeDriver: true,
    }).start();
  }, [currentPageIndex, slideAnim]);

  const handleAnswer = async (directAnswer?: string | string[]) => {
    if (!currentPage || isTransitioning) return;

    const currentState: PageInputState = {
      selectedAnswers,
      textAnswer,
      customAnswer,
      showCustomInput,
    };
    setPageStateMap(prev => ({ ...prev, [currentPageIndex]: currentState }));

    let answer: string | string[] = '';
    
    // Use direct answer if provided (for single-select navigation)
    if (directAnswer !== undefined) {
      answer = directAnswer;
    } else {
      // Check if this is guru selection
      const hasImages = currentPage.answers?.some(a => a.imageUrl);
      const isGuruSelection = hasImages && currentPage.title.toLowerCase().includes('guide');
      
      // Validate answer based on page type
      switch (currentPage.type) {
        case 'age':
        case 'gender':
          if (selectedAnswers.length === 0) {
            HapticFeedback.error();
            Alert.alert('Please select an option');
            return;
          }
          answer = selectedAnswers[0];
          break;
        
        case 'closed_question':
          // Special handling for guru selection with custom input
          if (isGuruSelection) {
            // Combine selected gurus and custom guru
            const selectedGuruNames = selectedAnswers;
            if (customAnswer.trim()) {
              // If there are selected gurus AND custom input, combine them
              answer = selectedGuruNames.length > 0 
                ? [...selectedGuruNames, customAnswer.trim()].join(', ')
                : customAnswer.trim();
            } else if (selectedGuruNames.length > 0) {
              answer = selectedGuruNames.join(', ');
            } else {
              HapticFeedback.error();
              Alert.alert('Please select at least one guru or enter a custom name');
              return;
            }
          } else if (selectedAnswers.length === 0 && !customAnswer && !currentPage.allowSkip) {
            // Only show error if question doesn't allow skip
            HapticFeedback.error();
            Alert.alert('Please select at least one option');
            return;
          } else {
            // Handle custom input for other questions (like books)
            let finalAnswers = [...selectedAnswers];
            if (customAnswer && showCustomInput) {
              finalAnswers = finalAnswers.filter(a => a !== '__CUSTOM__');
              finalAnswers.push(`Custom: ${customAnswer}`);
            }
            
            // For multi-select questions, join all answers including custom
            // Allow empty array for skippable questions
            answer = currentPage.maxSelections && currentPage.maxSelections > 1 
              ? finalAnswers 
              : finalAnswers[0];
          }
          break;
        
        case 'open_question':
          if (!textAnswer.trim()) {
            HapticFeedback.error();
            Alert.alert('Please provide an answer');
            return;
          }
          answer = textAnswer.trim();
          break;
        
        case 'interrupter':
          // No answer needed for interrupter pages
          break;
        
        default:
          return;
      }
    }

    // Set transitioning state to prevent race conditions
    setIsTransitioning(true);

    // Haptic feedback for successful progression
    HapticFeedback.medium();

    // Record the answer (except for interrupters)
    if (currentPage.type !== 'interrupter') {
      const qa: QuestionAnswer = {
        question: currentPage.title,
        answer,
        type: currentPage.type,
      };

      setPageAnswerMap(prev => {
        const updated = { ...prev, [currentPageIndex]: qa };
        const ordered = Object.keys(updated)
          .map(Number)
          .sort((a, b) => a - b)
          .map(key => updated[key]);
        setQuestionsAndAnswers(ordered);
        return updated;
      });
      
      // Log the question answered
      const questionSlug = getQuestionSlug(currentPage.title, currentPage.type);
      loggingService.log(onboardingQuestionAnswered(questionSlug, answer));
    }

    // Use requestAnimationFrame to ensure smooth transitions
    requestAnimationFrame(() => {
      // Clear state for next page
      setSelectedAnswers([]);
      setTextAnswer('');
      setCustomAnswer('');
      setShowCustomInput(false);
      setIsProcessingSingleSelect(false);
      
      // Delay navigation to allow UI to settle
      setTimeout(async () => {
        try {
          // Move to next page or complete
          if (currentPageIndex < pages.length - 1) {
            // Move to next page
            setCurrentPageIndex(currentPageIndex + 1);
          } else {
            completeOnboardingFlow();
          }
        } finally {
          setIsTransitioning(false);
        }
      }, 150); // Reduced from 200ms to 150ms for better responsiveness
    });
  };

  const fetchNextPages = async () => {
    // No server-driven follow-ups anymore; complete flow
    completeOnboardingFlow();
  };

  const startBackgroundLoading = async () => {
    // Deprecated: server-driven follow-ups removed. Nothing to load.
    return;
  };

  const handleLearnByDoingContinue = () => {
    console.log('handleLearnByDoingContinue called', { questionsLoaded, currentPageIndex });
    if (questionsLoaded) {
      console.log('Questions loaded - advancing to next page');
      setCurrentPageIndex(prev => prev + 1);
      setShowLearnByDoingPage(false);
    } else {
      console.log('Questions not loaded yet - waiting');
    }
  };

  const completeOnboardingFlow = () => {
    // Log onboarding completed
    const duration = Date.now() - onboardingStartTime.current;
    const totalSteps = Object.keys(pageAnswerMap).length;
    loggingService.log(onboardingCompleted('discovery_v2', duration, totalSteps));
    
    navigation.replace('CalculationScreen', { questionsAndAnswers });
  };

  const handleRecordingStateChange = (recording: boolean, starting: boolean, processing: boolean) => {
    setIsRecording(recording);
    setIsStarting(starting);
    setIsProcessing(processing);
  };

  const handleToggleAnswer = (answerId: string) => {
    if (!currentPage || isProcessingSingleSelect || isTransitioning) return;

    // Haptic feedback on selection
    HapticFeedback.selection();

    if (currentPage.maxSelections && currentPage.maxSelections > 1) {
      // Multi-select
      if (selectedAnswers.includes(answerId)) {
        setSelectedAnswers(selectedAnswers.filter(id => id !== answerId));
      } else {
        if (selectedAnswers.length < currentPage.maxSelections) {
          setSelectedAnswers([...selectedAnswers, answerId]);
        } else {
          HapticFeedback.warning();
          Alert.alert(`You can select up to ${currentPage.maxSelections} options`);
        }
      }
      
      // Handle custom input toggle
      if (answerId === '__CUSTOM__') {
        setShowCustomInput(!showCustomInput);
        if (showCustomInput) {
          setCustomAnswer('');
        }
      }
    } else {
      // Single select - show selection then auto-advance
      setSelectedAnswers([answerId]);
      setIsProcessingSingleSelect(true);
      
      // Use requestAnimationFrame to ensure UI updates before navigation
      requestAnimationFrame(() => {
        // Auto-advance after showing selection with reduced delay
        setTimeout(() => {
          handleAnswer(answerId); // Pass the answer directly
        }, 150); // Reduced from 200ms to 150ms for better responsiveness
      });
    }
  };

  const handleBack = () => {
    if (currentPageIndex === 0 || isTransitioning) return;

    // Haptic feedback for back navigation
    HapticFeedback.light();

    const previousIndex = currentPageIndex - 1;

    setPageStateMap(prev => ({
      ...prev,
      [currentPageIndex]: {
        selectedAnswers,
        textAnswer,
        customAnswer,
        showCustomInput,
      },
    }));

    setPageAnswerMap(prev => {
      const nextMap = { ...prev };
      Object.keys(nextMap).forEach(key => {
        if (Number(key) >= currentPageIndex) {
          delete nextMap[Number(key)];
        }
      });

      const ordered = Object.keys(nextMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map(key => nextMap[key]);
      setQuestionsAndAnswers(ordered);
      return nextMap;
    });

    const previousState = pageStateMap[previousIndex];
    setSelectedAnswers(previousState?.selectedAnswers ?? []);
    setTextAnswer(previousState?.textAnswer ?? '');
    setCustomAnswer(previousState?.customAnswer ?? '');
    setShowCustomInput(previousState?.showCustomInput ?? false);
    setIsProcessingSingleSelect(false);
    setIsTransitioning(false);
    setCurrentPageIndex(previousIndex);
  };

  const renderPage = () => {
    // Show LearnByDoingPage if we're in that state
    if (showLearnByDoingPage) {
      return (
        <Animated.View
          style={{
            flex: 1,
            opacity: slideAnim,
            transform: [
              {
                translateX: slideAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [24, 0],
                }),
              },
            ],
          }}
        >
          <LearnByDoingPage
            page={{ title: 'Learn by Doing', type: 'learn_by_doing' }}
            onAnswer={handleLearnByDoingContinue}
            isLoading={isLoadingMoreQuestions}
            onStartLoading={startBackgroundLoading}
          />
        </Animated.View>
      );
    }

    if (!currentPage) return null;

    const pageContent = (() => {
      switch (currentPage.type) {
        case 'age':
        case 'gender':
        case 'closed_question':
          return renderClosedQuestion();
        case 'open_question':
          return renderOpenQuestion();
        case 'interrupter':
          return renderInterrupter();
        default:
          return null;
      }
    })();

    return (
      <Animated.View
        style={{
          flex: 1,
          opacity: slideAnim,
          transform: [
            {
              translateX: slideAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [24, 0],
              }),
            },
          ],
        }}
      >
        {pageContent}
      </Animated.View>
    );
  };

  const renderClosedQuestion = () => {
    if (!currentPage || !currentPage.answers) return null;

    const isMultiSelect = currentPage.maxSelections && currentPage.maxSelections > 1;
    const hasImages = currentPage.answers.some(a => (a as any).image || a.imageUrl);
    const isGuruSelection = hasImages && currentPage.title.toLowerCase().includes('guide');

    return (
      <KeyboardAvoidingView 
        style={styles.pageContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        enabled={true}
      >
        <View style={styles.headerContainer}>
          <Text variant="heading2" align="center" style={styles.title}>
            {currentPage.title}
          </Text>
          {currentPage.subtitle && (
            <Text variant="heading3" color="secondary" align="center" style={styles.subtitle}>
              {currentPage.subtitle}
            </Text>
          )}
        </View>

        <StableScrollView 
          style={styles.answersContainer} 
          preventAutoScroll={true}
          keyboardShouldPersistTaps="handled"
        >
          {hasImages ? (
            // Grid layout for guru selection
            <>
              <View style={styles.guruGrid}>
                {currentPage.answers.map((answer, index) => (
                  <Pressable
                    key={answer.id || `answer-${index}`}
                    style={styles.guruCardContainer}
                    onPress={() => handleToggleAnswer(answer.id || `answer-${index}`)}
                  >
                    <View style={styles.guruImageContainer}>
                      {(answer as any).image ? (
                        <Image 
                          source={(answer as any).image}
                          style={[
                            styles.guruImage,
                            selectedAnswers.includes(answer.id || `answer-${index}`) && styles.guruImageSelected
                          ]}
                        />
                      ) : (
                        <Image 
                          source={{ uri: answer.imageUrl || '' }} 
                          defaultSource={require('../../assets/onboarding/guru-placeholder.webp')}
                          style={[
                            styles.guruImage,
                            selectedAnswers.includes(answer.id || `answer-${index}`) && styles.guruImageSelected
                          ]}
                        />
                      )}
                      {selectedAnswers.includes(answer.id || `answer-${index}`) && (
                        <View style={styles.guruCheckmark}>
                          <Text style={styles.guruCheckmarkText}>✓</Text>
                        </View>
                      )}
                    </View>
                    <Text style={
                      selectedAnswers.includes(answer.id || `answer-${index}`) 
                        ? { ...styles.guruName, ...styles.guruNameSelected }
                        : styles.guruName
                    }>
                      {answer.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
              
              {/* Custom guru input for guru selection */}
              {isGuruSelection && (
                <View style={styles.customGuruContainer}>
                  <Text variant="body1" style={styles.customGuruLabel}>
                    Or write your own:
                  </Text>
                  <View style={styles.customGuruInputWrapper}>
                    <TextInput
                      style={styles.customGuruInput}
                      placeholder="Type here"
                      placeholderTextColor={Theme.colors.textSecondary}
                      value={customAnswer}
                      onChangeText={(text) => {
                        setCustomAnswer(text);
                        // Don't clear selections - allow both predefined and custom
                      }}
                    />
                  </View>
                </View>
              )}
            </>
          ) : (
            // List layout for regular questions
            <View style={styles.optionsList}>
              {currentPage.answers.map((answer, index) => (
                <Pressable
                  key={answer.id || `answer-${index}`}
                  style={[
                    styles.optionButton,
                    selectedAnswers.includes(answer.id || `answer-${index}`) && styles.optionButtonSelected,
                    isProcessingSingleSelect && selectedAnswers.includes(answer.id || `answer-${index}`) && styles.optionButtonProcessing,
                  ]}
                  onPress={() => handleToggleAnswer(answer.id || `answer-${index}`)}
                  disabled={isProcessingSingleSelect}
                >
                  <Text style={
                    selectedAnswers.includes(answer.id || `answer-${index}`) 
                      ? { ...styles.optionText, ...styles.optionTextSelected }
                      : styles.optionText
                  }>
                    {answer.label}
                  </Text>
                  {isMultiSelect && selectedAnswers.includes(answer.id || `answer-${index}`) && (
                    <Text style={styles.checkIcon}>✓</Text>
                  )}
                  {!isMultiSelect && selectedAnswers.includes(answer.id || `answer-${index}`) && (
                    <Text style={styles.checkIcon}>✓</Text>
                  )}
                </Pressable>
              ))}
              
              {currentPage.allowCustomInput && (
                <>
                  <Pressable
                    style={[
                      styles.optionButton,
                      showCustomInput && styles.optionButtonSelected,
                    ]}
                    onPress={() => {
                      handleToggleAnswer('__CUSTOM__');
                    }}
                  >
                    <Text style={
                      showCustomInput 
                        ? { ...styles.optionText, ...styles.optionTextSelected }
                        : styles.optionText
                    }>
                      {currentPage.customInputLabel || 'Other (please specify)'}
                    </Text>
                    {showCustomInput && <Text style={styles.checkIcon}>✓</Text>}
                  </Pressable>
                  
                  {showCustomInput && (
                    <TextInput
                      style={styles.customInput}
                      placeholder={currentPage.customInputPlaceholder || 'Please specify...'}
                      value={customAnswer}
                      onChangeText={(text) => {
                        setCustomAnswer(text);
                        // Don't clear selections - allow both predefined and custom
                      }}
                      multiline
                    />
                  )}
                </>
              )}
            </View>
          )}
        </StableScrollView>

        {/* Only show continue button for multi-select questions or questions with custom input */}
        {(isMultiSelect || currentPage.allowCustomInput) && (
          <View style={[styles.closedQuestionFooter, { paddingBottom: Math.max(insets.bottom, 20) }]}>
            <Button
              title={isMultiSelect && selectedAnswers.length > 0 
                ? `Continue (${selectedAnswers.length} selected)`
                : 'Continue'
              }
              onPress={() => handleAnswer()}
              disabled={!currentPage.allowSkip && selectedAnswers.length === 0 && !customAnswer}
            />
            {currentPage.allowSkip && (
              <Pressable
                onPress={() => handleAnswer()}
                style={styles.skipButton}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={styles.skipButtonText}>Skip</Text>
              </Pressable>
            )}
          </View>
        )}
      </KeyboardAvoidingView>
    );
  };

  const renderOpenQuestion = () => {
    if (!currentPage) return null;

    const isAudioBusy = isRecording || isStarting || isProcessing;
    const isNameQuestion = currentPage.title.toLowerCase().includes('call you');
    const isButtonDisabled = textAnswer.trim().length === 0 || isAudioBusy;

    return (
      <KeyboardAvoidingView 
        style={styles.pageContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        enabled={true}
      >
        <ScrollView 
          ref={scrollViewRef}
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.headerContainer}>
            <Text variant="heading2" align="center" style={styles.title}>
              {currentPage.title}
            </Text>
            {currentPage.subtitle && (
              <Text variant="heading3" color="secondary" align="center" style={styles.subtitle}>
                {currentPage.subtitle}
              </Text>
            )}
          </View>

          <View style={styles.textInputWrapper}>
            <View style={styles.textInputContainer}>
              <TextInput
                style={styles.textInput}
                placeholder={currentPage.placeholder || 'Share what brought you here...'}
                placeholderTextColor={Theme.colors.textDisabled}
                value={textAnswer}
                onChangeText={(text) => {
                  // console.log('TextInput changed:', text);
                  setTextAnswer(text);
                }}
                multiline={!isNameQuestion}
                textAlignVertical="top"
                maxLength={currentPage.maxLength}
              />
            </View>
          </View>

          {!isNameQuestion && (
            <View style={styles.audioRecorderContainer}>
              <AudioRecorder
                onTranscription={(text) => {
                  // Append to existing text or replace if empty
                  const currentText = textAnswer || '';
                  const newText = currentText ? `${currentText} ${text}` : text;
                  setTextAnswer(newText);
                }}
                onRecordingStateChange={handleRecordingStateChange}
                placeholder="Or use voice input"
                maxDuration={120}
              />

              {currentPage.maxLength && (
                <Text variant="caption" color="secondary" align="right" style={styles.charCount}>
                  {textAnswer.length} / {currentPage.maxLength}
                </Text>
              )}
            </View>
          )}

          <View style={styles.openQuestionSpacer} />
        </ScrollView>

        <View style={[styles.openQuestionFooter, { paddingBottom: Math.max(insets.bottom, 20) }]}>
          <Button
            key={`button-${textAnswer.length}`}
            title={isAudioBusy ? `Continue (${isStarting ? 'Starting...' : isProcessing ? 'Processing...' : 'Recording...'})` : 'Continue'}
            onPress={() => handleAnswer()}
            disabled={isButtonDisabled}
          />
        </View>
      </KeyboardAvoidingView>
    );
  };

  const renderInterrupter = () => {
    if (!currentPage) return null;

    const isFirstPage = currentPageIndex === 0;
    const isNotAlonePage = currentPage.title.toLowerCase().includes('not alone');
    const containerStyle = isFirstPage 
      ? [styles.pageContainer, styles.firstPageContainer] 
      : styles.pageContainer;

    // Determine which image to use
    const getImageSource = () => {
      const customImage = (currentPage as any)?.image;
      if (customImage) {
        return customImage;
      }
      if (isFirstPage) {
        return require('../../assets/onboarding/onboarding1.webp');
      } else if (currentPage.title.includes('Great start!')) {
        return require('../../assets/onboarding/onboarding2.webp');
      } else if (currentPage.title.includes('Almost there')) {
        return require('../../assets/onboarding/onboarding3.webp');
      } else if (isNotAlonePage) {
        return require('../../assets/onboarding/interruptor-notalone.webp');
      } else {
        return require('../../assets/onboarding/first-page.webp');
      }
    };

    return (
      <KeyboardAvoidingView 
        style={containerStyle}
        behavior="padding"
        enabled={false}
      >
        {/* Background image positioned absolutely at bottom */}
        <Image 
          source={getImageSource()} 
          style={isFirstPage ? styles.firstPageImage : styles.interrupterImage}
          resizeMode="contain"
        />

        {/* Content layer on top of image */}
        <View style={styles.interrupterContent}>
          <Text variant="heading2" align="center" style={styles.interrupterTitle}>
            {currentPage.title}
          </Text>
          {currentPage.subtitle && (
            <Text 
              variant="body1" 
              color="secondary" 
              align={isFirstPage ? "left" : "center"} 
              style={[styles.interrupterSubtitle, isFirstPage && { textAlign: 'left' }]}
            >
              {currentPage.subtitle}
            </Text>
          )}
        </View>

        <View style={[styles.interrupterFooter, { paddingBottom: Math.max(insets.bottom, 20) }]}>
          <Button
            title={currentPage.buttonText || 'Continue'}
            onPress={() => handleAnswer()}
          />
        </View>
      </KeyboardAvoidingView>
    );
  };

  if (loading && !showLearnByDoingPage) {
    return (
      <SafariOptimizedContainer>
        <OnboardingPageContainer>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Theme.colors.primary} />
          </View>
        </OnboardingPageContainer>
      </SafariOptimizedContainer>
    );
  }

  return (
    <SafariOptimizedContainer>
      <OnboardingPageContainer>
        <View style={styles.topBar}>
          {currentPageIndex > 0 && (
            <Pressable 
              onPress={handleBack} 
              style={styles.backButton}
              disabled={isTransitioning}
              hitSlop={8}
              accessibilityLabel="Go back"
            >
              <Ionicons 
                name="chevron-back" 
                size={24} 
                color={isTransitioning ? Theme.colors.textDisabled : Theme.colors.textPrimary} 
              />
            </Pressable>
          )}
        </View>
        {renderPage()}
      </OnboardingPageContainer>
    </SafariOptimizedContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff9ee',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff9ee',
  },
  pageContainer: {
    flex: 1,
    width: '100%',
    paddingVertical: Theme.spacing.xxxl,
    backgroundColor: '#fff9ee',
  },
  topBar: {
    width: '100%',
    paddingHorizontal: 24,
    paddingTop: Platform.select({
      android: (StatusBar.currentHeight || 0) + Theme.spacing.md,
      default: Theme.spacing.md,
    }),
    paddingBottom: Theme.spacing.sm,
    alignItems: 'flex-start',
  },
  backButton: {
    paddingVertical: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.sm,
  },
  headerContainer: {
    width: '100%',
    paddingBottom: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    lineHeight: 32,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#3a3f47',
    textAlign: 'center',
    width: '100%',
  },
  subtitle: {
    fontSize: 17,
    lineHeight: 27,
    fontFamily: Theme.typography.fontFamily.regular,
    color: '#565b63',
    textAlign: 'center',
    width: '100%',
  },
  answersContainer: {
    flex: 1,
    width: '100%',
    paddingHorizontal: 24,
    paddingBottom: 120, // Space for absolute footer
  },
  optionsList: {
    gap: 10,
    alignSelf: 'stretch',
  },
  optionButton: {
    padding: 24,
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 24,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    borderColor: '#d8dbe7',
  },
  optionButtonSelected: {
    backgroundColor: '#fff',
    borderColor: Theme.colors.primary,
    borderWidth: 2,
  },
  optionButtonProcessing: {
    backgroundColor: '#fff',
    borderColor: Theme.colors.primary,
    borderWidth: 2,
  },
  optionText: {
    fontSize: 18,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: '#132871',
    flex: 1,
  },
  optionTextSelected: {
    color: '#132871',
    fontFamily: Theme.typography.fontFamily.regular,
  },
  checkIcon: {
    fontSize: 18,
    color: Theme.colors.primary,
    fontWeight: 'bold',
  },
  customInput: {
    backgroundColor: Theme.colors.surface,
    padding: Theme.spacing.xxl,
    borderRadius: Theme.borderRadius.xl,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    marginTop: Theme.spacing.sm,
    minHeight: 80,
    fontSize: 18,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
  },
  guruGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.sm,
  },
  guruCardContainer: {
    alignItems: 'center',
    width: '30%',
    marginBottom: Theme.spacing.xl,
  },
  guruImageContainer: {
    position: 'relative',
  },
  guruImage: {
    width: 92,
    height: 92,
    borderRadius: 46,
    opacity: 0.8,
  },
  guruImageSelected: {
    opacity: 1,
  },
  guruCheckmark: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: Theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  guruCheckmarkText: {
    color: Theme.colors.textInverse,
    fontSize: 12,
    fontWeight: 'bold',
  },
  guruName: {
    marginTop: Theme.spacing.sm,
    fontSize: 12,
    lineHeight: 19,
    fontWeight: '300',
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    width: 100,
  },
  guruNameSelected: {
    color: Theme.colors.primary,
    fontWeight: '400',
  },
  textInputWrapper: {
    width: '100%',
    paddingHorizontal: 24,
  },
  textInputContainer: {
    borderRadius: 24,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#d8dbe7',
    minHeight: 134,
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginHorizontal: 24,
  },
  textInput: {
    flex: 1,
    fontSize: 18,
    lineHeight: 26,
    fontFamily: Theme.typography.fontFamily.regular,
    color: '#132871',
    textAlignVertical: 'top',
    paddingTop: 0,
    paddingBottom: 0,
  },
  audioRecorderContainer: {
    width: '100%',
    paddingHorizontal: 24,
    gap: Theme.spacing.md,
  },
  charCount: {
    // Uses default caption styling
  },
  closedQuestionFooter: {
    width: '100%',
    paddingTop: 8,
    // paddingBottom is set dynamically using safe area insets
    paddingHorizontal: 24,
    gap: Theme.spacing.md,
  },
  skipButton: {
    paddingVertical: Theme.spacing.sm,
    alignSelf: 'center',
  },
  skipButtonText: {
    fontSize: 16,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.regular,
    textAlign: 'center',
  },
  interrupterFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.01)',
    paddingTop: 8,
    // paddingBottom is set dynamically using safe area insets
    paddingHorizontal: 24,
    zIndex: 2, // Ensure button is on top of everything
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  openQuestionFooter: {
    paddingTop: 8,
    // paddingBottom is set dynamically using safe area insets
    paddingHorizontal: 24,
    backgroundColor: '#fff9ee',
  },
  openQuestionSpacer: {
    height: Theme.spacing.xl,
  },
  spacer: {
    flex: 1,
  },
  interrupterContent: {
    flex: 1,
    paddingTop: 40,
    paddingHorizontal: 42,
    paddingBottom: 16,
    alignItems: 'center',
    gap: 16,
    zIndex: 1, // Ensure content is above image
  },
  interrupterTitle: {
    fontSize: 30,
    lineHeight: 38,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#3a3f47',
    textAlign: 'center',
    alignSelf: 'stretch',
  },
  interrupterSubtitle: {
    fontSize: 17,
    lineHeight: 27,
    fontFamily: Theme.typography.fontFamily.regular,
    color: '#565b63',
    textAlign: 'center',
    alignSelf: 'stretch',
  },
  interrupterImage: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: 300,
  },
  firstPageContainer: {
    backgroundColor: '#FFF5DF',
  },
  firstPageImage: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    height: undefined,
    aspectRatio: 1,
  },
  customGuruContainer: {
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.xxl,
    gap: Theme.spacing.sm,
  },
  customGuruLabel: {
    // Uses variant styling
  },
  customGuruInputWrapper: {
    backgroundColor: Theme.colors.surfaceSecondary,
    borderRadius: Theme.borderRadius.sm,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
  },
  customGuruInput: {
    fontSize: 18,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    minHeight: 26,
  },
}); 